/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import PaginatedListState, { type PaginatedListParams, type PaginatedListRequestParams } from 'flarum/common/states/PaginatedListState';
import ItemList from 'flarum/common/utils/ItemList';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import type { ApiResponsePlural } from 'flarum/common/Store';
import type Bookshelf from '../../common/models/Bookshelf';

export default class BookshelvesListState extends PaginatedListState<Bookshelf> {
	// Raises the pageSize default; also re-declared public so `new` works (TS2674).
	public constructor(params: PaginatedListParams = {}, page: number = 1, pageSize: number = 18) {
		super(params, page, pageSize);
	}

	get type(): string {
		return 'bookshelves';
	}

	protected requestParams(): PaginatedListRequestParams {
		const showAll: boolean = this.params.showAll ?? false;

		const params = { ...super.requestParams() }; // Clone for immutability

		params.sort = this.sortMap()[m.route.param('sort')];

		if (showAll) {
			return params;
		}

		const user: string | undefined = this.params.filter?.user ?? app.session.user?.id();

		params.filter = {
			...params.filter,
			user,
		} as Record<string, string>;

		return params;
	}

	/** URL sort key (also the translation key) to API sort value. */
	sortMap(): Record<string, string> {
		const map: Record<string, string> = {};

		Object.keys(this.sortMapItems().toObject()).forEach((key) => {
			const item = this.sortMapItems().get(key);
			map[key] = item;
		});

		return map;
	}

	sortMapItems(): ItemList<string> {
		const items = new ItemList<string>();

		items.add('latest', 'of_bookshelves.updatedAt', 100);
		items.add('newest', '-of_bookshelves.createdAt', 81);
		items.add('oldest', 'of_bookshelves.createdAt', 80);
		items.add('name_az', 'name', 71);
		items.add('name_za', '-name', 70);
		items.add('mostArticles', '-articleCount', 61);
		items.add('leastArticles', 'articleCount', 60);

		return items;
	}

	changeSort(sort: string): void {
		const params = this.params;

		delete params.page;
		delete params.showAll;

		if (sort === Object.keys(this.sortMap())[0]) {
			delete params.sort;
		} else {
			params.sort = sort;
		}

		const username = m.route.param('username');
		if (username) {
			params.username = username;
			delete params.filter.user;
		}

		setRouteWithForcedRefresh(app.route(app.current.get('routeName'), params));
	}

	// Widened to `public` so components can read the loaded bookshelves.
	public override getAllItems(): Bookshelf[] {
		return super.getAllItems();
	}

	protected loadPage(page: number = 1): Promise<ApiResponsePlural<Bookshelf>> {
		const preloadedBookshelves = app.preloadedApiDocument<Bookshelf[]>();

		if (preloadedBookshelves) {
			this.initialLoading = false;
			return Promise.resolve(preloadedBookshelves);
		}

		return super.loadPage(page);
	}
}
